import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'

import { sortItems, getAccessibility, collisionDetection } from '../lib'

const useDefaultSensors = () => useSensors(
  useSensor(MouseSensor),
  useSensor(TouchSensor),
  useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  })
)

/**
 * VerticalSortableContext creates context for vertical list of sortable items
 *
 * @example
 * const SortableItem = ({ item, children }) => {
 *   const {
 *     sortable: {
 *       isDragging,
 *       attributes,
 *       listeners,
 *       setNodeRef
 *     },
 *     itemStyle
 *   } = useAdvancedSortable({
 *     id: item.id,
 *   })
 *
 *   return (
 *     <Sortable ref={setNodeRef} isDragging={isDragging} {...itemStyle}>
 *       <DragHandler {...attributes} {...listeners} />
 *       {JSON.stringify(item)}
 *     </Sortable>
 *   )
 * }
 *
 * const SortableList = () => {
 *   const [items, setItems] = useState([{id: 1}, {id: 2}, {id: 3}])
 *
 *   return (
 *     <VerticalSortableContext items={items} setItems={setItems}>
 *       {items.map((item) => (
 *         <SortableItem item={item} key={item.id} />
 *       ))}
 *     </VerticalSortableContext>
 *   )
 * }
 */
export const VerticalSortableContext = ({ items, setItems, children }) => (
  <DndContext
    accessibility={getAccessibility(items)}
    sensors={useDefaultSensors()}
    collisionDetection={collisionDetection}
    onDragEnd={(event) => {
      const sorted = sortItems(items, event)

      if (sorted) {
        setItems(sorted)
      }
    }}
    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
  >
    <SortableContext
      items={items}
      strategy={verticalListSortingStrategy}
    >
      {children}
    </SortableContext>
  </DndContext>
)
